/**
 * web/src/lib/workflowPath.ts
 * Where does "open this workflow" land? Speaker onboarding defaults to the
 * speaker hub (/speakers/:eventId/:itemId) — the journey view doubles as the
 * onboarding flow, so the generic step-list detail page is the wrong default
 * for it. Everything else (sponsor, test) keeps /workflows/:id.
 *
 * The generic detail view stays reachable for speaker workflows via
 * `/workflows/:id?view=steps` (WorkflowDetail's redirect honors the param) —
 * the speaker hub uses it to link PRIOR attempts, which would otherwise
 * redirect straight back to the hub.
 */
import type { Workflow } from '../api/types';

/** The fields the path decision needs — every list/detail payload has them. */
export type WorkflowPathInput = Pick<Workflow, 'id' | 'type' | 'eventId' | 'context'>;

/**
 * The speaker-hub path for a speaker_onboarding workflow, or null when it
 * isn't one (or its context has no monday itemId to address the hub with).
 */
export function speakerHubPath(w: WorkflowPathInput): string | null {
  if (w.type !== 'speaker_onboarding') return null;
  const speaker = w.context?.speaker;
  const itemId =
    speaker && typeof speaker === 'object'
      ? String((speaker as Record<string, unknown>).itemId ?? '').trim()
      : '';
  if (!itemId || !w.eventId) return null;
  return `/speakers/${w.eventId}/${encodeURIComponent(itemId)}`;
}

/** The default landing path for a workflow: speaker hub when applicable, else the step-list detail. */
export function workflowPath(w: WorkflowPathInput): string {
  // Wave 2 ops automation runs are reviewed on their own page (editable rows).
  if (w.type.startsWith('ops_')) return `/ops/runs/${w.id}`;
  return speakerHubPath(w) ?? `/workflows/${w.id}`;
}
